// import components
import { Box, Typography, styled } from '@mui/material';

// import constants
import { ORANGE, WHITE } from 'constants/colors';

/**
 * One stray appointment, styled to read like the cards in afsprakenplanning: a coloured accent, the
 * activity title, and — when there are any — the other participants, with a recurrence marker pinned in
 * the bottom-right corner. A card with no participants is just its title line. Right-clicking opens the
 * actions menu. A non-editable appointment is greyed out and set in italic, the same read-only treatment
 * afsprakenplanning gives an appointment that cannot be edited. While its right-click menu is open the
 * card is marked with `data-selected` and gets a black ring that wraps the whole card — the accent line
 * included — so it is clear which appointment is acted on, the same way afsprakenplanning outlines the
 * selected appointment in black.
 */
export const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isReadOnly'
})<{ isReadOnly?: boolean }>(({ theme, isReadOnly }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(0.75, 1),
  // Rounded like the afsprakenplanning appointment cards (8px), rather than the tighter cell radius.
  borderRadius: theme.spacing(1),
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderLeft: '3px solid',
  borderLeftColor: theme.palette.primary.main,
  backgroundColor: theme.palette.background.paper,
  minWidth: 0,
  overflow: 'hidden',
  ...(isReadOnly && {
    fontStyle: 'italic',
    backgroundColor: theme.palette.grey[50],
    borderLeftColor: theme.palette.grey[400]
  }),
  // A ring drawn just outside the card (box-shadow, so it never nudges the layout) that wraps the whole
  // card, accent line included, and lifts it above its neighbours while selected.
  '&[data-selected="true"]': {
    boxShadow: `0 0 0 2px ${theme.palette.common.black}`,
    zIndex: 1
  }
}));

// True black, since the theme's default text colour is a dark navy rather than black; a non-editable
// appointment fades to grey instead, matching afsprakenplanning's read-only treatment.
export const ActivityName = styled(Typography, {
  shouldForwardProp: (prop) =>
    prop !== 'isReadOnly' && prop !== 'reserveMarkerSpace'
})<{ isReadOnly?: boolean; reserveMarkerSpace?: boolean }>(
  ({ theme, isReadOnly, reserveMarkerSpace }) => ({
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1.3,
    color: isReadOnly ? theme.palette.grey[700] : theme.palette.common.black,
    // The signal markers sit top-right, on the title's row, so whenever there are any, reserve room on
    // the right of the title so a long title truncates before them rather than running under them.
    ...(reserveMarkerSpace && { paddingRight: theme.spacing(2) })
  })
);

/**
 * Participants read as plain text under the activity — never bold, so only the activity stands out.
 * Rendered only when there are participants; wraps onto a second line and then clamps with an ellipsis.
 * The right padding keeps the text clear of the recurrence marker.
 */
export const Participant = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  lineHeight: 1.3,
  paddingRight: theme.spacing(2),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
}));

/**
 * Fills the card (its padding box) and acts as a size-query container for the markers, so the stack can
 * respond to how much room the card actually has. `pointer-events: none` keeps it clear of the card's
 * own hover and right-click. When the container is too short to stack the markers without crowding the
 * card, they collapse into a single count badge — the same overflow afsprakenplanning falls back to when
 * its icons don't fit. The threshold matches two 14px markers plus their gap and a little breathing room.
 * Any stack of two or more markers collapses (a lone marker always fits and keeps its icon), so a third
 * signal added later folds into the badge on its own without touching this rule.
 */
export const SignalOverlay = styled('span')({
  position: 'absolute',
  inset: 0,
  containerType: 'size',
  containerName: 'signals',
  pointerEvents: 'none',
  '@container signals (height < 38px)': {
    '& .signal-stack:not([data-signal-count="1"]) .signal-icon': {
      display: 'none'
    },
    '& .signal-stack:not([data-signal-count="1"]) .overflow-badge': {
      display: 'flex'
    }
  }
});

/**
 * The signal markers afsprakenplanning uses, anchored in the top-right corner and flowing downward, so
 * they read top-to-bottom in afsprakenplanning's order — the recurrence marker first, then the note
 * marker — the same corner afsprakenplanning stacks them in. Drawn in true black, the same black
 * afsprakenplanning draws its own markers in; `& path { fill: currentColor }` drives the exception SVG,
 * which otherwise ships with a hard-coded black fill.
 */
export const SignalStack = styled('span')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(0.5),
  top: theme.spacing(0.5),
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  lineHeight: 0,
  color: theme.palette.common.black,
  '& svg': { width: 14, height: 14 },
  '& path': { fill: 'currentColor' }
}));

/** One marker in the stack; carries the accessible label describing that signal. */
export const SignalIcon = styled('span')({
  display: 'inline-flex',
  lineHeight: 0
});

/**
 * The overflow fallback afsprakenplanning uses: an orange circle with the number of markers, shown only
 * (via the container query above) when the markers cannot fit. Hidden by default so it never doubles up
 * with the markers themselves. The colours are afsprakenplanning's own ORANGE/WHITE constants, so the
 * badge reads identically in both places. Its `font-style: normal` keeps it upright on a read-only
 * (italic) card.
 */
export const OverflowBadge = styled('span')(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: 14,
  height: 14,
  borderRadius: '50%',
  backgroundColor: ORANGE,
  color: WHITE,
  fontSize: 9,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1,
  fontStyle: 'normal'
}));
